import { Autocomplete, TextField, Checkbox as MuiCheckbox, Typography } from '@mui/material'
import MuiIconCheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank'
import MuiIconCheckBox from '@mui/icons-material/CheckBox'
import { ProvidersWorkUnitsInboxListResponse } from '@fireflyhealth/api'

import { useState } from 'react'
import type { CarePlanTemplateId } from './InboxService'
type TypeFilterOption = {
  type: 'Care Plan' | 'Case Category'
  /** A care plan with no template groups under "Other", which the wire spells `None`. */
  id: CarePlanTemplateId
  label: string
  count: number
}

interface Props {
  data: ProvidersWorkUnitsInboxListResponse | undefined
  currentSelections?: SelectedTypeFilters
  onClose: (selectedFilters: SelectedTypeFilters) => void
}

export type SelectedTypeFilters = Pick<TypeFilterOption, 'type' | 'id' | 'label'>[]

const checkboxIcon = <MuiIconCheckBoxOutlineBlank fontSize="small" />
const checkedCheckboxIcon = <MuiIconCheckBox fontSize="small" />

const TodoTypeSelect = ({ data, onClose, currentSelections }: Props) => {
  /**
   * Allow multi-selecting Types
   * Trigger a callback function when selection is complete
   * to avoid multiple API calls during multi-select
   */

  const [typeFilters, setTypeFilters] = useState<SelectedTypeFilters>(currentSelections ?? [])
  const onSelectionReady = (selected: SelectedTypeFilters) => {
    onClose(selected)
  }

  // Keep the temporary state here updated with the parent component's selections
  const [parentSelections, setParentSelections] = useState(currentSelections)
  if (currentSelections !== parentSelections) {
    setParentSelections(currentSelections)
    if (currentSelections) setTypeFilters(currentSelections)
  }

  return (
    <Autocomplete
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      multiple
      disableCloseOnSelect
      filterSelectedOptions
      size="small"
      options={[
        ...(data?.aggregates?.case__category?.flatMap(option =>
          option.category_id != null && option.category__title != null
            ? [
                {
                  type: 'Case Category',
                  id: option.category_id,
                  label: option.category__title,
                  count: option.count ?? 0,
                } satisfies TypeFilterOption,
              ]
            : [],
        ) ?? []),
        ...(data?.aggregates?.careplan__autocreated_from?.map(
          option =>
            ({
              type: 'Care Plan',
              id: option.autocreated_from_id ?? 'None',
              label: option.autocreated_from__title ?? 'Other',
              count: option.count ?? 0,
            }) satisfies TypeFilterOption,
        ) ?? []),
      ]}
      groupBy={option => option.type}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.type == value.type && option.id == value.id}
      renderInput={params => <TextField {...params} label={'Type'} />}
      renderOption={({ key, ...props }, option, { selected }) => (
        <li key={key} {...props}>
          <MuiCheckbox
            icon={checkboxIcon}
            checkedIcon={checkedCheckboxIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.label}

          <Typography sx={{ color: 'secondary.main', display: 'inline' }}>
            &nbsp;{option.count}
          </Typography>
        </li>
      )}
      value={typeFilters?.map(filter => ({
        ...filter,
        count: 0, // Doesn't actually matter when rendering the value.
      }))}
      onChange={(event, value) => {
        setTypeFilters(value)
        if (event.type == 'click' && ['path', 'svg'].includes((event.target as Node).nodeName)) {
          // Click events on a selected option's "x" element
          // Or the entire input's "X" icon to clear all selections
          // can be fired with the input unfocused
          // so we won't get a later blur event, and onClose wouldn't be fired
          onSelectionReady(value)
        }
      }}
      onClose={() => onSelectionReady(typeFilters)}
    />
  )
}

export default TodoTypeSelect
